import { useState, type ReactNode } from 'react';
import { Button } from './ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from './ui/sheet';
import { PageLink } from './PageLink';
import { Menu, ArrowRight, ArrowUpRight } from 'lucide-react';
import logoImage from '../assets/TurfAI-logo.png';
import { platformTopics } from '../data/platform';
import { accelerators } from '../data/accelerators';
import { siteConfig } from '../config/siteConfig';

interface NavigationProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

// One-liners for the Platform menu (fuller copy lives in data/platform.ts).
const platformNavDesc: Record<string, string> = {
  why: 'Six reasons, not a feature list',
  agents: 'Agents that survive production',
  squads: 'Specialist teams, sequential or manager-led',
  rag: 'Answers with citations',
  chatbots: 'Cited answers, embedded anywhere',
  governance: 'Caps, prompt review, audit trails',
  'edge-ai': 'Small models on your hardware',
  integrations: 'Native, OAuth2 and MCP connectors',
  'domain-packs': 'Nine live, one in design',
};

const platformLinks = [
  ...platformTopics.map((t) => ({ id: t.pageId, title: t.name, desc: platformNavDesc[t.slug] ?? t.eyebrow })),
  { id: 'security', title: 'Security & Trust', desc: 'OWASP GenAI risks, Data Shield' },
];

const industryLinks = [
  { id: 'solutions-healthcare', title: 'Healthcare', desc: 'Chart audit, intake, documentation' },
  { id: 'solutions-bfsi', title: 'BFSI', desc: 'Loan documents, KYC/AML, credit memos' },
  { id: 'solutions-insurance', title: 'Insurance', desc: 'Claims, underwriting, complaints' },
  { id: 'solutions-hr-tech', title: 'HR Tech', desc: 'Hiring, onboarding, Ask HR' },
  { id: 'solutions-it-ops', title: 'IT Ops', desc: 'Alert correlation, RCA, auto-remediation' },
  { id: 'solutions-legal', title: 'Legal', desc: 'Petition replies, NDAs, timelines' },
  { id: 'solutions-telecom', title: 'Telecom', desc: 'SMS template compliance' },
  { id: 'solutions-manufacturing', title: 'Manufacturing', desc: 'Shift logs, rejects, training' },
  { id: 'solutions-education', title: 'Education', desc: 'Admissions you can explain' },
  { id: 'solutions-invoice-processing', title: 'Invoice Processing', desc: 'Accounts payable on autopilot' },
];

const acceleratorLinks = accelerators.map((a) => ({ id: a.pageId, title: a.name, desc: a.menuDesc }));

const resourceLinks = [
  { id: 'resources', title: 'Case studies', desc: 'TurfAI in production, in eight sectors' },
  { href: siteConfig.urls.developers, title: 'Developer docs', desc: 'APIs, task types, triggers and guides' },
  { href: siteConfig.urls.userGuide, title: 'User guide', desc: 'How every module works, in depth' },
];

// Full-width panel flush under the 72px header (+1px rule). No zoom animation, no shadow.
const panelClass =
  '!fixed !left-0 !right-0 !top-[73px] !mt-0 !w-screen !rounded-none !border-0 !border-b !border-softblack-tint-20 !bg-white !p-0 data-[state=open]:!animate-none data-[state=closed]:!animate-none';

const triggerBase =
  '!h-[72px] !rounded-none !bg-transparent !px-0 !text-[15px] !font-medium border-b-2 hover:!bg-transparent focus:!bg-transparent data-[state=open]:!bg-transparent data-[state=open]:text-royal-indigo';

const menuItemClass = 'group block w-full rounded-lg px-3 py-2.5 text-left hover:bg-indigo-tint-05 aria-[current=page]:bg-indigo-tint-05';

function MenuItemBody({ title, desc, external }: { title: string; desc: string; external?: boolean }) {
  return (
    <>
      <span className="flex items-center gap-1 font-display text-[16px] font-semibold text-dark-indigo group-hover:text-royal-indigo">
        {title}
        {external && <ArrowUpRight className="h-3.5 w-3.5" aria-label="(opens in a new tab)" />}
      </span>
      <span className="block text-[13px] text-soft-black">{desc}</span>
    </>
  );
}

/** A mega-menu entry: a site page (client-side link) or an external site (new tab). */
function MenuItem({ title, desc, page, href, go, currentPage }: { title: string; desc: string; page?: string; href?: string; go: (p: string) => void; currentPage: string }) {
  return (
    <NavigationMenuLink asChild>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={menuItemClass}><MenuItemBody title={title} desc={desc} external /></a>
      ) : (
        <PageLink page={page!} onNavigate={go} currentPage={currentPage} className={menuItemClass}><MenuItemBody title={title} desc={desc} /></PageLink>
      )}
    </NavigationMenuLink>
  );
}

function Panel({ intro, children, feature }: { intro: ReactNode; children: ReactNode; feature: ReactNode }) {
  return (
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-12">
        <div className="col-span-3 py-8 pr-8">{intro}</div>
        <div className="col-span-6 border-l border-softblack-tint-20 py-6 px-6">{children}</div>
        <div className="col-span-3 border-l border-softblack-tint-20 py-6 pl-8">{feature}</div>
      </div>
    </div>
  );
}

function Feature({ label, metric, body, cta, page, go }: { label: string; metric: string; body: string; cta: string; page: string; go: (p: string) => void }) {
  return (
    <div className="rounded-lg bg-indigo-tint-05 p-6">
      <p className="text-[13px] font-bold text-royal-indigo">{label}</p>
      <p className="mt-3 border-l-4 border-vibrant-orange pl-3 font-display text-[44px] font-bold leading-none text-dark-indigo">{metric}</p>
      <p className="mt-3 text-[14px] leading-relaxed text-soft-black">{body}</p>
      <NavigationMenuLink asChild>
        <PageLink page={page} onNavigate={go} className="mt-4 inline-flex !flex-row items-center gap-1.5 text-[14px] font-bold text-royal-indigo hover:text-dark-indigo">
          {cta} <ArrowRight className="h-4 w-4" />
        </PageLink>
      </NavigationMenuLink>
    </div>
  );
}

export function Navigation({ currentPage, onNavigate, onNavigateToDemo }: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);

  const go = (page: string) => {
    onNavigate(page);
    setIsOpen(false);
  };
  const active = (on: boolean) => (on ? 'text-dark-indigo border-vibrant-orange' : 'text-soft-black hover:text-royal-indigo border-transparent');

  return (
    <header className="sticky top-0 z-50 w-full border-b border-softblack-tint-20 bg-white">
      <div className="container mx-auto flex h-[72px] items-center gap-10 px-4">
        <PageLink page="turfai" onNavigate={go} aria-label="TurfAI home" className="flex items-center gap-2.5 text-xl font-semibold">
          <img src={logoImage} alt="TurfAI Logo" className="h-9 w-auto" />
          <span className="turfai-wordmark text-soft-black" aria-hidden="true">TurfAI</span>
        </PageLink>

        {/* Desktop */}
        <nav className="hidden lg:flex h-full items-center">
          <NavigationMenu viewport={false} className="h-full">
            <NavigationMenuList className="h-full gap-7">
              <NavigationMenuItem>
                <NavigationMenuTrigger className={`${triggerBase} ${active(currentPage === 'platform' || currentPage.startsWith('platform-') || currentPage === 'security')}`}>
                  Platform
                </NavigationMenuTrigger>
                <NavigationMenuContent className={panelClass}>
                  <Panel
                    intro={
                      <>
                        <p className="font-display text-[22px] font-semibold leading-snug text-dark-indigo">One platform to design, run and govern agentic workflows.</p>
                        <p className="mt-3 text-[14px] leading-relaxed text-soft-black">Visual builder, agents, RAG and governance on one auditable layer.</p>
                        <NavigationMenuLink asChild>
                          <PageLink page="platform" onNavigate={go} currentPage={currentPage} className="mt-5 inline-flex !flex-row items-center gap-1.5 text-[15px] font-bold text-royal-indigo hover:text-dark-indigo">
                            Platform overview <ArrowRight className="h-4 w-4" />
                          </PageLink>
                        </NavigationMenuLink>
                      </>
                    }
                    feature={<Feature label="In production" metric="11×" body="Clinical throughput from the Healthcare Domain Pack, at 99.4% chart-audit accuracy." cta="See the Healthcare pack" page="solutions-healthcare" go={go} />}
                  >
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {platformLinks.map((l) => <MenuItem key={l.id} title={l.title} desc={l.desc} page={l.id} go={go} currentPage={currentPage} />)}
                    </div>
                  </Panel>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={`${triggerBase} ${active(currentPage.startsWith('solutions'))}`}>
                  Solutions
                </NavigationMenuTrigger>
                <NavigationMenuContent className={panelClass}>
                  <Panel
                    intro={
                      <>
                        <p className="font-display text-[22px] font-semibold leading-snug text-dark-indigo">The problems in your industry. Not adjacent ones.</p>
                        <p className="mt-3 text-[14px] leading-relaxed text-soft-black">Each industry runs on a Domain Pack that is live in production today.</p>
                        <NavigationMenuLink asChild>
                          <PageLink page="solutions" onNavigate={go} currentPage={currentPage} className="mt-5 inline-flex !flex-row items-center gap-1.5 text-[15px] font-bold text-royal-indigo hover:text-dark-indigo">
                            All solutions <ArrowRight className="h-4 w-4" />
                          </PageLink>
                        </NavigationMenuLink>
                      </>
                    }
                    feature={<Feature label="In production" metric="24×" body="Employee productivity at a B2B lender running the BFSI Domain Pack." cta="See the BFSI pack" page="solutions-bfsi" go={go} />}
                  >
                    <p className="px-3 pb-1 text-[13px] font-bold text-royal-indigo">By industry</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {industryLinks.map((l) => <MenuItem key={l.id} title={l.title} desc={l.desc} page={l.id} go={go} currentPage={currentPage} />)}
                    </div>
                  </Panel>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={`${triggerBase} ${active(acceleratorLinks.some((l) => l.id === currentPage))}`}>
                  Accelerators &amp; Tools
                </NavigationMenuTrigger>
                <NavigationMenuContent className={panelClass}>
                  <Panel
                    intro={
                      <>
                        <p className="font-display text-[22px] font-semibold leading-snug text-dark-indigo">Ready-to-run applications, built on TurfAI.</p>
                        <p className="mt-3 text-[14px] leading-relaxed text-soft-black">Run them as they are, or open any workflow behind them and make it yours.</p>
                      </>
                    }
                    feature={<Feature label="In production" metric="3" body="Question types in every StageOne assessment — multiple choice, spoken and coding." cta="See StageOne" page="stageone" go={go} />}
                  >
                    <div className="grid grid-cols-1 gap-y-1">
                      {acceleratorLinks.map((l) => <MenuItem key={l.id} title={l.title} desc={l.desc} page={l.id} go={go} currentPage={currentPage} />)}
                    </div>
                  </Panel>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={`${triggerBase} ${active(currentPage === 'resources' || currentPage.startsWith('case-'))}`}>Resources</NavigationMenuTrigger>
                <NavigationMenuContent className={panelClass}>
                  <Panel
                    intro={
                      <>
                        <p className="font-display text-[22px] font-semibold leading-snug text-dark-indigo">Learn it, then build on it.</p>
                        <p className="mt-3 text-[14px] leading-relaxed text-soft-black">Case studies from the team, and the docs your engineers will use.</p>
                      </>
                    }
                    feature={<Feature label="In production" metric="10×" body="HR workflow efficiency at an HR services provider running employee support on TurfAI." cta="See the HR Tech pack" page="solutions-hr-tech" go={go} />}
                  >
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {resourceLinks.map((l) => (
                        <MenuItem key={l.title} title={l.title} desc={l.desc} page={'id' in l ? l.id : undefined} href={'href' in l ? l.href : undefined} go={go} currentPage={currentPage} />
                      ))}
                    </div>
                  </Panel>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <PageLink page="about" onNavigate={go} currentPage={currentPage} className={`flex h-[72px] items-center border-b-2 text-[15px] font-medium ${active(currentPage === 'about')}`}>
                  About
                </PageLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <Button size="lg" className="hidden sm:inline-flex h-10 px-5 text-[15px]" onClick={onNavigateToDemo}>
            Schedule a demo
          </Button>

          {/* Mobile */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" className="lg:hidden px-2" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] overflow-y-auto bg-white p-6">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <nav className="mt-8 space-y-6" aria-label="Main">
                {[
                  { label: 'Platform', items: [{ id: 'platform', title: 'Platform overview' }, ...platformLinks] },
                  { label: 'Solutions', items: [{ id: 'solutions', title: 'All solutions' }, ...industryLinks] },
                  { label: 'Accelerators & Tools', items: acceleratorLinks },
                ].map((g) => (
                  <div key={g.label}>
                    <p className="mb-2 text-[13px] font-bold text-royal-indigo">{g.label}</p>
                    {g.items.map((l) => (
                      <PageLink key={l.id} page={l.id} onNavigate={go} currentPage={currentPage} className="block w-full rounded px-2 py-2.5 text-left font-medium text-dark-indigo hover:bg-indigo-tint-05 aria-[current=page]:bg-indigo-tint-05 aria-[current=page]:text-royal-indigo">
                        {l.title}
                      </PageLink>
                    ))}
                  </div>
                ))}
                <div>
                  <p className="mb-2 text-[13px] font-bold text-royal-indigo">Resources</p>
                  <PageLink page="resources" onNavigate={go} currentPage={currentPage} className="block w-full rounded px-2 py-2.5 text-left font-medium text-dark-indigo hover:bg-indigo-tint-05 aria-[current=page]:bg-indigo-tint-05 aria-[current=page]:text-royal-indigo">Case studies</PageLink>
                  <a href={siteConfig.urls.developers} target="_blank" rel="noopener noreferrer" className="block w-full rounded px-2 py-2.5 text-left font-medium text-dark-indigo hover:bg-indigo-tint-05 aria-[current=page]:bg-indigo-tint-05 aria-[current=page]:text-royal-indigo">Developer docs</a>
                  <a href={siteConfig.urls.userGuide} target="_blank" rel="noopener noreferrer" className="block w-full rounded px-2 py-2.5 text-left font-medium text-dark-indigo hover:bg-indigo-tint-05 aria-[current=page]:bg-indigo-tint-05 aria-[current=page]:text-royal-indigo">User guide</a>
                </div>
                <PageLink page="about" onNavigate={go} currentPage={currentPage} className="block w-full rounded px-2 py-2.5 text-left font-medium text-dark-indigo hover:bg-indigo-tint-05 aria-[current=page]:bg-indigo-tint-05 aria-[current=page]:text-royal-indigo">About</PageLink>
                <Button className="w-full h-11" onClick={() => { onNavigateToDemo(); setIsOpen(false); }}>Schedule a demo</Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
